/**
 * DocumentView — issued-document view (design §4): PDF-faithful preview (left)
 * + payment progress, action grid, e-Invoice (IRP) panel, and timeline (right).
 * Document detail, line items and timeline load live from the billing service;
 * seller identity comes from the session org + org profile. Role-aware actions
 * (Void/Write-off ADMIN-only).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive, MAX_CONTENT_WIDTH } from '../utils/responsive';
import { useSession } from '../context/sessionContext';
import SkIcon from '../components/skIcon';
import StatusPill from '../components/statusPill';
import { DocRow, fmtMoney, money, DEFAULT_CURRENCY } from '../utils/format';
import { showToast } from '../components/skToast';
import RecordPaymentModal from '../components/modals/recordPaymentModal';
import EInvoiceModal from '../components/modals/eInvoiceModal';
import { ReasonModal, SendDocumentModal } from '../components/modals/documentActionModals';
import {
  acceptQuote,
  convertDocument,
  declineQuote,
  EinvoiceStatus,
  exportEinvoice,
  getDocument,
  getDocumentEvents,
  getDocumentPdf,
  getEinvoiceStatus,
  voidDocument,
  writeOffDocument,
} from '../services/billing/document.service';
import { createRazorpayLink } from '../services/billing/payment.service';
import { DocType } from '../schema/billing/common';
import type { EditorPrefill } from './documentEditor';
import { documentActions } from '../utils/documentActions';
import { getOrgProfile } from '../services/billing/accountant.service';

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});

/**
 * Amount in words.
 *
 * Indian-system grouping (lakh/crore) and the word "Rupees" are correct for
 * INR and wrong for everything else, so both follow the document's currency:
 * a non-INR document groups in thousands/millions and names its own currency.
 * Amount-in-words is a legal element on an Indian tax invoice, which is why
 * this is currency-aware rather than simply dropped for other markets.
 */
const CURRENCY_WORD: Record<string, string> = {
  INR: 'Rupees', AED: 'Dirhams', GBP: 'Pounds', USD: 'Dollars',
};

function words(n: number, currency: string = 'INR'): string {
  return currency === 'INR' ? indianWords(n) : westernWords(n, currency);
}

function westernWords(n: number, currency: string): string {
  n = Math.round(Math.abs(n));
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = (x: number): string => (x < 20 ? a[x]! : b[Math.floor(x / 10)]! + (x % 10 ? ' ' + a[x % 10] : ''));
  const three = (x: number): string => { const h = Math.floor(x / 100), r = x % 100; return (h ? a[h] + ' Hundred' + (r ? ' ' : '') : '') + (r ? two(r) : ''); };
  const unit = CURRENCY_WORD[currency] ?? currency;
  if (n === 0) return `${unit} Zero Only`;
  let out = '';
  const bn = Math.floor(n / 1e9); n %= 1e9;
  const mn = Math.floor(n / 1e6); n %= 1e6;
  const th = Math.floor(n / 1000); n %= 1000;
  if (bn) out += three(bn) + ' Billion ';
  if (mn) out += three(mn) + ' Million ';
  if (th) out += three(th) + ' Thousand ';
  if (n) out += three(n);
  return `${unit} ` + out.trim() + ' Only';
}

/** Indian-system amount in words (lakh/crore). */
function indianWords(n: number): string {
  n = Math.round(Math.abs(n));
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = (x: number): string => (x < 20 ? a[x] : b[Math.floor(x / 10)] + (x % 10 ? ' ' + a[x % 10] : ''));
  const three = (x: number): string => { const h = Math.floor(x / 100), r = x % 100; return (h ? a[h] + ' Hundred' + (r ? ' ' : '') : '') + (r ? two(r) : ''); };
  if (n === 0) return 'Rupees Zero Only';
  let out = '';
  const cr = Math.floor(n / 10000000); n %= 10000000;
  const lk = Math.floor(n / 100000); n %= 100000;
  const th = Math.floor(n / 1000); n %= 1000;
  if (cr) out += three(cr) + ' Crore ';
  if (lk) out += three(lk) + ' Lakh ';
  if (th) out += three(th) + ' Thousand ';
  if (n) out += three(n);
  return 'Rupees ' + out.trim() + ' Only';
}

const EVENT_LABEL: Record<string, string> = {
  CREATED: 'Created',
  ISSUED: 'Issued',
  SENT: 'Sent',
  DELIVERED: 'Delivered',
  VIEWED: 'Viewed by customer',
  PAYMENT_RECORDED: 'Payment recorded',
  PAID: 'Paid in full',
  VOIDED: 'Voided',
  WRITTEN_OFF: 'Written off',
  PAYMENT_ALLOCATED: 'Payment received',
  PAYMENT_UNALLOCATED: 'Payment removed',
  ACCEPTED: 'Accepted by customer',
  DECLINED: 'Declined by customer',
  CONVERTED: 'Converted to invoice',
  DRAFT_REVISED: 'Draft edited',
  EINVOICE_EXPORTED: 'e-Invoice JSON exported',
  IRN_RECORDED: 'IRN recorded',
  DELETED: 'Deleted',
};
/** "SOME_EVENT" → "Some event", for any event this map does not name yet. */
const humanize = (code: string): string => {
  const t = code.toLowerCase().replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const VOID_REASONS: string[][] = [
  ['DATA_ENTRY_ERROR', 'Entered by mistake'],
  ['DUPLICATE', 'Duplicate document'],
  ['ORDER_CANCELLED', 'Order cancelled by customer'],
  ['OTHER', 'Other'],
];
const WRITE_OFF_REASONS: string[][] = [
  ['BAD_DEBT', 'Customer will not pay (bad debt)'],
  ['SMALL_BALANCE', 'Small balance not worth chasing'],
  ['SETTLEMENT', 'Agreed settlement'],
  ['OTHER', 'Other'],
];
// The document types the IRP takes (server: EINVOICEABLE_TYPES).
const EINVOICE_TYPES = ['INVOICE', 'CREDIT_NOTE', 'DEBIT_NOTE'];

/** Premium hover-reactive action button in the right-rail grid. */
const ActionBtn: React.FC<{ a: { act: string; label: string; icon: string; primary?: boolean; danger?: boolean }; onPress: () => void }> = ({ a, onPress }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [h, setH] = useState(false);
  const tint = a.primary ? '#fff' : a.danger ? colors.danger : colors.ink2;
  return (
    <Pressable
      onPress={onPress}
      style={{
        width: a.primary ? '100%' : '47%',
        height: 38,
        borderRadius: 10,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        borderWidth: 1,
        backgroundColor: a.primary ? colors.accent : a.danger && h ? colors.dangerSoft : h ? colors.paper2 : colors.card,
        borderColor: a.primary ? colors.transparent : a.danger && h ? colors.dangerLine : h ? colors.lineStrong : colors.line,
        ...(a.primary
          ? web({ backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: h ? `0 7px 16px -4px ${colors.accent}99` : `0 4px 10px -3px ${colors.accent}66`, transform: h ? 'translateY(-1px)' : 'translateY(0)' })
          : {}),
        ...web({ transition: 'all .14s ease', cursor: 'pointer' }),
      }}
      {...(isWeb ? { onMouseEnter: () => setH(true), onMouseLeave: () => setH(false) } : {})}
    >
      <SkIcon name={a.icon} size={15} color={tint} strokeWidth={2} />
      <Text style={{ fontFamily: a.primary ? font.sansSemibold : font.sansMedium, fontSize: 12.5, fontWeight: a.primary ? '600' : '500', color: tint }}>{a.label}</Text>
    </Pressable>
  );
};

/** What the view asks the shell to open: a document's editor, or another document. */
export type OpenEditor = (target: { doc: DocRow | null; docType: DocType; prefill?: EditorPrefill }) => void;

const DocumentView: React.FC<{ doc: DocRow; onBack: () => void; onOpenEditor?: OpenEditor }> = ({ doc, onBack, onOpenEditor }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const { isWide } = useResponsive();
  const { role, org } = useSession();
  const admin = role === 'ADMIN';

  const [full, setFull] = useState<any | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [sellerGstin, setSellerGstin] = useState<string>('');
  const [einvoice, setEinvoice] = useState<EinvoiceStatus | null>(null);
  const [reloadKey, setReloadKey] = useState(0); // bump to re-fetch after a mutation (e.g. payment)

  useEffect(() => {
    let alive = true;
    Promise.allSettled([getDocument(doc.id), getDocumentEvents(doc.id), getOrgProfile()])
      .then(([d, e, p]) => {
        if (!alive) return;
        if (d.status === 'fulfilled') setFull(d.value);
        if (e.status === 'fulfilled' && Array.isArray(e.value)) setEvents(e.value);
        if (p.status === 'fulfilled') setSellerGstin((p.value as any)?.gstin || '');
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [doc.id, reloadKey]);

  // e-Invoice state comes from the server — only for the types the IRP takes.
  useEffect(() => {
    if (!EINVOICE_TYPES.includes(doc.type) || doc.status === 'DRAFT') { setEinvoice(null); return; }
    let alive = true;
    getEinvoiceStatus(doc.id)
      .then((r) => { if (alive) setEinvoice(r); })
      .catch(() => { if (alive) setEinvoice(null); });
    return () => { alive = false; };
  }, [doc.id, doc.type, doc.status, reloadKey]);

  // The currency is stamped on the document server-side and is immutable,
  // so every amount on this screen renders in it — never in a constant.
  const cur: string = full?.currency || DEFAULT_CURRENCY;
  const grand = full ? money(full.grand_total) : Math.abs(doc.amount);
  const subtotal = full ? money(full.subtotal) : 0;
  const taxTotal = full ? money(full.tax_total) : 0;
  const discountTotal = full ? money(full.discount_total) : 0;
  // Prefer the freshly-fetched doc so the balance/progress update after a payment;
  // fall back to the list row's balance until `full` loads.
  const balance =
    full && full.amount_settled !== undefined
      ? Math.max(0, money(full.grand_total) - money(full.amount_settled))
      : Math.abs(doc.balance);
  const paid = grand - balance;
  const paidPct = grand > 0 ? Math.round((paid / grand) * 100) : 0;
  const lines: any[] = Array.isArray(full?.lines) ? full.lines : [];

  // The freshly loaded status wins over the list row, so the badge and the
  // actions follow a payment / void the moment it happens.
  const status: string = full?.status ?? doc.status;
  const settled = full ? money(full.amount_settled) : grand - balance;
  const actions = useMemo(
    () => documentActions({ type: doc.type, status, balance, settled, admin }),
    [doc.type, status, balance, settled, admin],
  );

  const [payOpen, setPayOpen] = React.useState(false);
  const [eiOpen, setEiOpen] = React.useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [backHover, setBackHover] = useState(false);
  const [pdfHover, setPdfHover] = useState(false);

  const downloadPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const { file_name, content_base64 } = await getDocumentPdf(doc.id);
      const fileName = file_name || `${doc.number || 'document'}.pdf`;
      if (isWeb && typeof document !== 'undefined') {
        // Decode the base64 payload into a real PDF blob.
        const binary = atob(content_base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
      showToast('success', 'PDF downloaded', `${fileName} saved to your device`);
    } catch {
      showToast('danger', 'Could not generate PDF', 'Please try again in a moment.');
    } finally {
      setPdfBusy(false);
    }
  };

  const [sendOpen, setSendOpen] = useState(false);
  const [reasonFor, setReasonFor] = useState<'void' | 'write-off' | null>(null);
  const [busyAct, setBusyAct] = useState<string | null>(null);
  const reload = () => setReloadKey((k) => k + 1);

  /** A credit note against THIS invoice, pre-filled from it. */
  const openCreditNote = () => {
    if (!onOpenEditor || !full) return;
    onOpenEditor({
      doc: null,
      docType: 'CREDIT_NOTE',
      prefill: {
        linkedDocumentId: doc.id,
        linkedDocumentNumber: doc.number,
        contact: full.contact_id ? { id: full.contact_id, name: full.contact_name || doc.customer } : null,
        placeOfSupply: full.place_of_supply ?? undefined,
        currency: full.currency,
        lines: lines.map((l: any) => ({ description: l.description, hsn_sac: l.hsn_sac, quantity: String(l.quantity), unit_price: String(l.unit_price), tax_group_id: l.tax_group_id })),
      },
    });
  };

  /** Run a server action once, with the button showing it is working. */
  const run = async (act: string, fn: () => Promise<unknown>) => {
    if (busyAct) return;
    setBusyAct(act);
    try {
      await fn();
    } catch {
      // the service layer showed the server's reason
    } finally {
      setBusyAct(null);
    }
  };

  const onAction = (act: string) => {
    switch (act) {
      case 'record-payment':
        setPayOpen(true);
        return;
      case 'send':
        setSendOpen(true);
        return;
      case 'void':
      case 'write-off':
        setReasonFor(act);
        return;
      case 'credit-note':
        openCreditNote();
        return;
      case 'accept':
        run(act, async () => { await acceptQuote(doc.id); reload(); });
        return;
      case 'decline':
        run(act, async () => { await declineQuote(doc.id); reload(); });
        return;
      case 'convert':
        run(act, async () => {
          const inv: any = await convertDocument(doc.id);
          // Open the new invoice draft so it can be checked and issued.
          if (onOpenEditor && inv?.id) {
            onOpenEditor({
              doc: { id: inv.id, type: 'INVOICE', number: inv.doc_number || 'Draft', customer: inv.contact_name || doc.customer, sub: '', date: inv.doc_date, due: inv.due_date || '—', status: inv.status, amount: money(inv.grand_total), balance: money(inv.grand_total) },
              docType: 'INVOICE',
            });
          } else {
            reload();
          }
        });
        return;
      case 'payment-link':
        run(act, async () => {
          const link = await createRazorpayLink(doc.id, `${TYPE_NOUN[doc.type] || 'Invoice'} ${doc.number}`);
          const url = link?.short_url;
          let copied = false;
          if (url && isWeb && typeof navigator !== 'undefined' && navigator.clipboard) {
            copied = await navigator.clipboard.writeText(url).then(() => true, () => false);
          }
          showToast('success', copied ? 'Payment link copied' : 'Payment link created', url || '');
        });
        return;
      default:
    }
  };

  const confirmReason = async (reasonCode: string) => {
    if (reasonFor === 'void') await voidDocument(doc.id, reasonCode);
    else if (reasonFor === 'write-off') await writeOffDocument(doc.id, reasonCode);
    setReasonFor(null);
    reload();
  };

  /** Download the NIC INV-01 JSON the server built (and recorded as exported). */
  const doExportEinvoice = async (confirmPastWindow = false) => {
    const res = await exportEinvoice(doc.id, confirmPastWindow);
    if (isWeb && typeof document !== 'undefined') {
      const blob = new Blob([JSON.stringify(res.payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `einvoice-${doc.number}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
    showToast('success', 'e-Invoice JSON exported', 'Upload it on the IRP portal to get the IRN.');
    reload();
  };

  const fmtTs = (s: string): string => {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s || '';
    return d.toLocaleDateString();
  };
  const timeline = events.length
    ? events.map((e) => ({ title: EVENT_LABEL[e.event_type] || humanize(e.event_type), meta: fmtTs(e.created_at), done: true, icon: 'check' }))
    : [{ title: 'Created', meta: doc.date, done: true, icon: 'plus' }];

  const card = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, ...theme.shadow('sm') } as const;
  // Straight from the server: an IRN shows as captured only when there is one.
  const eiDone = !!einvoice?.irn;
  const eiExported = !eiDone && einvoice?.status === 'EXPORTED';
  const eiPast = !eiDone && !!einvoice?.past_window;
  const eiTone = eiDone
    ? { c: colors.success, label: 'IRN captured' }
    : eiPast
      ? { c: colors.danger, label: 'Past window' }
      : eiExported
        ? { c: colors.info, label: 'JSON exported' }
        : { c: colors.warn, label: 'Pending' };
  const eiPaid = eiDone;
  const colHead = { fontFamily: font.sans, fontSize: 10, color: colors.ink3, textTransform: 'uppercase' as const, letterSpacing: 0.4 };

  // Document-type-aware labels (so a Credit Note isn't titled "Tax Invoice").
  const TYPE_LABEL: Record<string, string> = { INVOICE: 'Tax Invoice', QUOTE: 'Quotation', CREDIT_NOTE: 'Credit Note', BILL: 'Bill', VENDOR_CREDIT: 'Vendor Credit', EXPENSE: 'Expense' };
  const TYPE_NOUN: Record<string, string> = { INVOICE: 'Invoice', QUOTE: 'Quote', CREDIT_NOTE: 'Credit note', BILL: 'Bill', VENDOR_CREDIT: 'Vendor credit', EXPENSE: 'Expense' };
  const variantLabel = full?.print_variant === 'BILL_OF_SUPPLY' ? 'Bill of Supply' : (TYPE_LABEL[doc.type] || 'Document');
  const dateNoun = TYPE_NOUN[doc.type] || 'Document';

  return (
    <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16 }}>
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Pressable
          onPress={onBack}
          style={{ width: 36, height: 36, borderWidth: 1, borderColor: backHover ? colors.lineStrong : colors.line, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: backHover ? colors.paper2 : colors.card, ...theme.shadow('sm'), ...web({ transition: 'all .14s ease', cursor: 'pointer' }) }}
          {...(isWeb ? { onMouseEnter: () => setBackHover(true), onMouseLeave: () => setBackHover(false) } : {})}
        >
          <SkIcon name="chevLeft" size={17} color={colors.ink2} strokeWidth={2.2} />
        </Pressable>
        <Text style={{ fontFamily: font.monoSemibold, fontSize: 19, fontWeight: '700', color: colors.ink, letterSpacing: -0.3 }}>{doc.number}</Text>
        <StatusPill status={status as DocRow['status']} />
        <Text style={{ fontFamily: font.sansMedium, fontSize: 12, color: colors.ink4 }}>{variantLabel}</Text>
        <View style={{ flex: 1 }} />
        <Pressable
          onPress={downloadPdf}
          disabled={pdfBusy}
          style={{ height: 38, paddingHorizontal: 16, borderWidth: 1, borderColor: pdfHover ? colors.lineStrong : colors.line, borderRadius: 10, flexDirection: 'row', alignItems: 'center', gap: 7, backgroundColor: pdfHover ? colors.paper2 : colors.card, ...theme.shadow('sm'), ...web({ transition: 'all .14s ease', cursor: 'pointer' }) }}
          {...(isWeb ? { onMouseEnter: () => setPdfHover(true), onMouseLeave: () => setPdfHover(false) } : {})}
        >
          <SkIcon name="download" size={15} color={colors.ink2} strokeWidth={2} />
          <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>{pdfBusy ? 'Preparing…' : 'Download PDF'}</Text>
        </Pressable>
      </View>

      <View style={{ flexDirection: isWide ? 'row' : 'column', gap: 16 }}>
        {/* PDF preview */}
        <View style={[card, { flex: isWide ? 1.5 : undefined, padding: 28 }]}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20 }}>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <View style={{ width: 40, height: 40, borderRadius: 11, backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center', ...theme.shadow('base'), ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})` }) }}>
                <SkIcon name="rupee" size={20} color="#fff" strokeWidth={2.6} />
              </View>
              <View>
                <Text style={{ fontFamily: font.sansBold, fontSize: 16, fontWeight: '700', color: colors.ink }}>{org?.name || 'Your organisation'}</Text>
                {!!sellerGstin && <Text style={{ fontFamily: font.mono, fontSize: 11, color: colors.ink3 }}>GSTIN {sellerGstin}</Text>}
              </View>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={{ fontFamily: font.sansBold, fontSize: 15, fontWeight: '700', color: colors.accent, letterSpacing: 0.5 }}>{variantLabel.toUpperCase()}</Text>
              <Text style={{ fontFamily: font.monoSemibold, fontSize: 13, color: colors.ink2, marginTop: 2 }}>{doc.number}</Text>
            </View>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 12, borderTopWidth: 1, borderBottomWidth: 1, borderColor: colors.line, marginBottom: 16 }}>
            <View>
              <Text style={[colHead, { marginBottom: 3 }]}>Bill to</Text>
              <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: colors.ink }}>{doc.customer}</Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink3 }}>{dateNoun} date <Text style={{ fontFamily: font.mono, color: colors.ink2 }}>{doc.date}</Text></Text>
              <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink3 }}>Due date <Text style={{ fontFamily: font.mono, color: colors.ink2 }}>{doc.due}</Text></Text>
            </View>
          </View>
          {/* line table */}
          <View style={{ flexDirection: 'row', borderBottomWidth: 1.5, borderColor: colors.ink3, paddingBottom: 7 }}>
            <Text style={[colHead, { flex: 1 }]}>Description</Text>
            <Text style={[colHead, { width: 70 }]}>HSN</Text>
            <Text style={[colHead, { width: 50, textAlign: 'right' }]}>Qty</Text>
            <Text style={[colHead, { width: 90, textAlign: 'right' }]}>Rate</Text>
            <Text style={[colHead, { width: 90, textAlign: 'right' }]}>Amount</Text>
          </View>
          {lines.length === 0 ? (
            <View style={{ paddingVertical: 16 }}>
              <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink4 }}>No line items.</Text>
            </View>
          ) : (
            lines.map((l, i) => (
              <View key={l.id || i} style={{ flexDirection: 'row', paddingVertical: 9, borderBottomWidth: 1, borderColor: colors.line }}>
                <Text style={{ flex: 1, fontFamily: font.sans, fontSize: 13, fontWeight: '500', color: colors.ink }}>{l.description}</Text>
                <Text style={{ width: 70, fontFamily: font.mono, fontSize: 12, color: colors.ink3 }}>{l.hsn_sac || '—'}</Text>
                <Text style={{ width: 50, textAlign: 'right', fontFamily: font.mono, fontSize: 12, color: colors.ink2 }}>{l.quantity}</Text>
                <Text style={{ width: 90, textAlign: 'right', fontFamily: font.mono, fontSize: 12, color: colors.ink2 }}>{fmtMoney(money(l.unit_price), cur)}</Text>
                <Text style={{ width: 90, textAlign: 'right', fontFamily: font.mono, fontSize: 13, color: colors.ink }}>{fmtMoney(money(l.line_total) || money(l.unit_price) * money(l.quantity), cur)}</Text>
              </View>
            ))
          )}
          {/* words + totals */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 14 }}>
            <View style={{ flex: 1, paddingRight: 16 }}>
              <Text style={[colHead, { marginBottom: 3 }]}>Amount in words</Text>
              <Text style={{ fontFamily: font.sans, fontSize: 12, fontStyle: 'italic', color: colors.ink2, lineHeight: 18 }}>{words(grand, cur)}</Text>
            </View>
            <View style={{ width: 260 }}>
              <View style={{ gap: 7, paddingHorizontal: 14, paddingTop: 12, paddingBottom: 12 }}>
                {[['Subtotal', fmtMoney(subtotal, cur)], ...(discountTotal > 0 ? [['Discount', '−' + fmtMoney(discountTotal, cur)] as [string, string]] : []), ['Tax', fmtMoney(taxTotal, cur)]].map(([l, v]) => (
                  <View key={l} style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text style={{ fontFamily: font.sans, fontSize: 12.5, color: colors.ink3 }}>{l}</Text>
                    <Text style={{ fontFamily: font.monoMedium, fontSize: 12.5, color: colors.ink2 }}>{v}</Text>
                  </View>
                ))}
              </View>
              {/* Total — accent band */}
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, paddingHorizontal: 14, borderRadius: 12, borderWidth: 1, borderColor: colors.accentLine, backgroundColor: colors.accentSoft, ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})` }) }}>
                <Text style={{ fontFamily: font.sansBold, fontSize: 14, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>Total</Text>
                <Text style={{ fontFamily: font.monoSemibold, fontSize: 19, fontWeight: '700', color: colors.accentInk, letterSpacing: -0.4 }}>{fmtMoney(grand, cur)}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Right rail */}
        <View style={{ flex: isWide ? 1 : undefined, gap: 14 }}>
          {/* No payment bar on a quote, nor on a voided document — nothing is owed. */}
          {doc.type !== 'QUOTE' && status !== 'VOID' && (
          <View style={[card, { padding: 16 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: paidPct >= 100 ? colors.success : paidPct > 0 ? colors.warn : colors.info }} />
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 12.5, fontWeight: '600', color: colors.ink }}>{balance > 0 ? (paidPct > 0 ? 'Partially paid' : 'Awaiting payment') : 'Fully paid'}</Text>
              </View>
              <Text style={{ fontFamily: font.monoMedium, fontSize: 12.5, color: colors.ink2 }}>{fmtMoney(paid, cur)} / {fmtMoney(grand, cur)}</Text>
            </View>
            <View style={{ height: 9, backgroundColor: colors.paper3, borderRadius: 999, overflow: 'hidden' }}>
              <View style={{ width: `${paidPct}%`, height: 9, borderRadius: 999, backgroundColor: paidPct >= 100 ? colors.success : paidPct > 0 ? colors.warn : colors.info, ...web({ backgroundImage: `linear-gradient(90deg, ${paidPct >= 100 ? colors.success : paidPct > 0 ? colors.warn : colors.info}, ${paidPct >= 100 ? colors.successLine : paidPct > 0 ? colors.warnLine : colors.infoLine})` }) }} />
            </View>
          </View>

          )}

          {actions.length > 0 && (
          <View style={[card, { padding: 12 }]}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {actions.map((a) => (
                <ActionBtn key={a.act} a={busyAct === a.act ? { ...a, label: 'Working…' } : a} onPress={() => onAction(a.act)} />
              ))}
            </View>
          </View>
          )}

          {/* e-Invoice (IRP) — only where the server says it applies */}
          {einvoice?.applicable && (
          <View style={[card, { padding: 16, backgroundColor: eiPast ? colors.dangerSoft : eiPaid ? colors.successSoft : colors.card, borderColor: eiPast ? colors.dangerLine : eiPaid ? colors.successLine : colors.line }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
                <View style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
                  <SkIcon name="fileText" size={15} color={colors.ink3} strokeWidth={2} />
                </View>
                <Text style={{ fontFamily: font.sansBold, fontSize: 14, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>e-Invoice (IRP)</Text>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, backgroundColor: eiPaid ? colors.successSoft : eiPast ? colors.dangerSoft : colors.warnSoft, borderWidth: 1, borderColor: eiPaid ? colors.successLine : eiPast ? colors.dangerLine : colors.warnLine }}>
                <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: eiTone.c }} />
                <Text style={{ fontFamily: font.sans, fontSize: 11, fontWeight: '500', color: eiTone.c }}>{eiTone.label}</Text>
              </View>
            </View>
            {!eiPast && (
              <View>
                <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink3, marginTop: 6, lineHeight: 17 }}>Generate the NIC INV-01 JSON to file with the IRP.</Text>
                {eiDone ? (
                  <Text style={{ fontFamily: font.mono, fontSize: 11, color: colors.ink3, marginTop: 6 }} numberOfLines={1}>IRN {einvoice?.irn}</Text>
                ) : (
                  <Pressable onPress={() => run('export', () => doExportEinvoice())} disabled={busyAct === 'export'} style={{ marginTop: 10, height: 32, borderWidth: 1, borderColor: colors.line, borderRadius: 5, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 6, backgroundColor: colors.paper2 }}>
                    <SkIcon name="download" size={13} color={colors.ink2} /><Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink2 }}>{busyAct === 'export' ? 'Exporting…' : eiExported ? 'Export JSON again' : 'Export JSON'}</Text>
                  </Pressable>
                )}
              </View>
            )}
            {eiPast && (
              <View>
                <Text style={{ fontFamily: font.sans, fontSize: 12, color: colors.danger, marginTop: 6, lineHeight: 17 }}>Outside the 30-day reporting window — the IRP will reject it.</Text>
                <Pressable onPress={() => setEiOpen(true)} style={{ marginTop: 10, height: 32, backgroundColor: colors.accent, borderRadius: 5, alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={{ fontFamily: font.sans, fontSize: 12, fontWeight: '500', color: '#fff' }}>Resolve e-invoice…</Text>
                </Pressable>
              </View>
            )}
          </View>
          )}

          <View style={[card, { padding: 16 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 14 }}>
              <View style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
                <SkIcon name="clock" size={15} color={colors.ink3} strokeWidth={2} />
              </View>
              <Text style={{ fontFamily: font.sansBold, fontSize: 14, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>Timeline</Text>
            </View>
            {timeline.map((t, i) => (
              <View key={i} style={{ flexDirection: 'row', gap: 10, marginBottom: i === timeline.length - 1 ? 0 : 12 }}>
                <View style={{ width: 24, height: 24, borderRadius: 12, borderWidth: 1.5, borderColor: t.done ? colors.successLine : colors.line, backgroundColor: t.done ? colors.successSoft : colors.paper2, alignItems: 'center', justifyContent: 'center' }}>
                  <SkIcon name={t.icon} size={12} color={t.done ? colors.success : colors.ink4} strokeWidth={2.4} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontFamily: font.sans, fontSize: 12, fontWeight: '500', color: colors.ink }}>{t.title}</Text>
                  <Text style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4 }}>{t.meta}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      </View>

      <RecordPaymentModal visible={payOpen} onClose={() => setPayOpen(false)} doc={doc} contactId={full?.contact_id} currency={full?.currency} onSaved={() => { setPayOpen(false); setReloadKey((k) => k + 1); }} />
      <EInvoiceModal visible={eiOpen} onClose={() => setEiOpen(false)} number={doc.number} issuedDate={doc.date} onGuidedFix={openCreditNote} onExportAnyway={() => doExportEinvoice(true)} />
      <SendDocumentModal visible={sendOpen} onClose={() => setSendOpen(false)} documentId={doc.id} documentNumber={doc.number} contactId={full?.contact_id} onSent={() => { setSendOpen(false); reload(); }} />
      <ReasonModal
        visible={reasonFor !== null}
        onClose={() => setReasonFor(null)}
        title={reasonFor === 'void' ? `Void ${doc.number}?` : `Write off ${doc.number}?`}
        subtitle={reasonFor === 'void' ? 'This cannot be undone' : `${fmtMoney(balance, cur)} outstanding`}
        warning={
          reasonFor === 'void'
            ? 'The document is cancelled and its ledger entries are reversed. The number stays used — issue a fresh document if you need one.'
            : `The unpaid ${fmtMoney(balance, cur)} is moved to Bad Debt and the invoice is closed. Use this only when the money will not come.`
        }
        reasons={reasonFor === 'void' ? VOID_REASONS : WRITE_OFF_REASONS}
        confirmText={reasonFor === 'void' ? 'Void document' : 'Write off'}
        busyText={reasonFor === 'void' ? 'Voiding…' : 'Writing off…'}
        onConfirm={confirmReason}
      />
    </View>
  );
};

export default DocumentView;
